import JSZip from "jszip";
import { SKILL_FILE_MAX_BYTES, SKILL_PACKAGE_MAX_BYTES } from "@prodgy/skills";

import { emLotes } from "@/shared/utils/emLotes";

/** Um arquivo do pacote, no formato que a api de publicação espera. */
export interface ArquivoDoPacote {
  path: string;
  content: string;
  encoding: "utf8" | "base64";
}

export interface PacoteLido {
  folderName: string;
  files: ArquivoDoPacote[];
}

/**
 * Extensões que o pacote carrega como texto. O resto vira base64.
 *
 * Lista explícita em vez de adivinhar pelo conteúdo: um `.png` que por acaso
 * comece com bytes legíveis seria gravado como texto e corromperia na volta.
 */
const TEXTO = new Set([
  "md",
  "markdown",
  "txt",
  "json",
  "yaml",
  "yml",
  "csv",
  "tsv",
  "js",
  "ts",
  "py",
  "sh",
  "sql",
  "html",
  "css",
  "xml",
  "toml",
  "ini",
  "env",
  "gitignore",
  "license",
]);

const ehTexto = (caminho: string) => {
  const extensao = caminho.split(".").pop()?.toLowerCase() ?? "";

  return TEXTO.has(extensao) || !caminho.includes(".");
};

/** Erro que a tela mostra como está, sem traduzir — já vem em português. */
export class PacoteInvalido extends Error {}

const paraBase64 = (bytes: Uint8Array) => {
  let binario = "";
  // Em blocos: `String.fromCharCode(...bytes)` estoura a pilha em arquivo
  // grande, e o limite por arquivo aqui é de 1 MB.
  for (let i = 0; i < bytes.length; i += 8192) {
    binario += String.fromCharCode(...bytes.subarray(i, i + 8192));
  }

  return btoa(binario);
};

/**
 * Descarta o que não é do pacote.
 *
 * Arrastar uma pasta de verdade traz lixo do sistema de arquivos e do editor
 * junto. Enviá-lo publicaria `.DS_Store` como material de apoio da skill.
 */
const ehLixo = (caminho: string) =>
  caminho.split("/").some(
    (parte) =>
      parte === ".DS_Store" ||
      parte === "Thumbs.db" ||
      parte === ".git" ||
      parte === "node_modules" ||
      parte === "__MACOSX",
  );

/**
 * O tamanho REAL do arquivo, em bytes.
 *
 * `content.length` conta unidades de UTF-16 no texto — um acento vale 1 e vale
 * 2 bytes — e caracteres de base64 no binário, que infla o número em um terço.
 * Os dois erram para lados diferentes: o texto passava do limite sem ser
 * barrado, e o binário era recusado antes da hora.
 */
const bytesDe = (file: ArquivoDoPacote): number => {
  if (file.encoding !== "base64") return new TextEncoder().encode(file.content).length;

  // Base64: cada 4 caracteres viram 3 bytes, menos o preenchimento no fim.
  const limpo = file.content.replace(/[\r\n=]/g, "");

  return Math.floor((limpo.length * 3) / 4);
};

const confereLimites = (files: ArquivoDoPacote[]) => {
  if (files.length === 0) {
    throw new PacoteInvalido("O pacote está vazio.");
  }

  const total = files.reduce((soma, file) => soma + bytesDe(file), 0);

  for (const file of files) {
    if (bytesDe(file) > SKILL_FILE_MAX_BYTES) {
      throw new PacoteInvalido(
        `O arquivo "${file.path}" passa do limite de ${Math.round(SKILL_FILE_MAX_BYTES / 1024)} KB.`,
      );
    }
  }

  if (total > SKILL_PACKAGE_MAX_BYTES) {
    throw new PacoteInvalido(
      `O pacote passa do limite de ${Math.round(SKILL_PACKAGE_MAX_BYTES / 1024 / 1024)} MB.`,
    );
  }
};

/**
 * Remove o diretório raiz comum dos caminhos.
 *
 * Um `.zip` feito de uma pasta traz tudo sob `minha-skill/`, e o leitor espera
 * os caminhos relativos à raiz do pacote — `SKILL.md`, não
 * `minha-skill/SKILL.md`. O nome descartado vira o `folderName`.
 */
const semRaiz = (caminhos: string[]): { raiz: string | null; corte: number } => {
  const primeiros = new Set(caminhos.map((caminho) => caminho.split("/")[0]));

  if (primeiros.size !== 1) return { raiz: null, corte: 0 };

  const raiz = [...primeiros][0]!;
  const temArquivoNaRaiz = caminhos.some((caminho) => !caminho.includes("/"));

  if (temArquivoNaRaiz) return { raiz: null, corte: 0 };

  return { raiz, corte: raiz.length + 1 };
};

/**
 * Lê uma pasta ESCOLHIDA pelo seletor de arquivos.
 *
 * O `<input webkitdirectory>` entrega um `FileList` plano, e o caminho relativo
 * de cada arquivo vem em `webkitRelativePath` — `minha-skill/SKILL.md`. O
 * primeiro segmento é a pasta escolhida e vira o `folderName`, como no `.zip`.
 *
 * Existe porque nem todo mundo arrasta: uma área que só aceita arrastar é um
 * beco sem saída para quem clica nela.
 */
export async function lerSelecao(selecionados: FileList): Promise<PacoteLido> {
  const lista = [...selecionados];
  if (lista.length === 0) throw new PacoteInvalido("Nenhum arquivo foi escolhido.");

  const caminhos = lista.map((arquivo) => arquivo.webkitRelativePath || arquivo.name);
  const { raiz, corte } = semRaiz(caminhos);

  // Leitura com janela: ler um arquivo por vez desperdiça o paralelismo do
  // navegador num pacote de dezenas de arquivos, e ler todos de uma vez traz o
  // pacote inteiro para a memória ao mesmo tempo. A ordem da lista é preservada.
  const aLer = lista
    .map((arquivo, indice) => ({ arquivo, bruto: caminhos[indice]!, path: caminhos[indice]!.slice(corte) }))
    .filter(({ path, bruto }) => path && !ehLixo(bruto));

  const files = await emLotes(aLer, async ({ arquivo, path }) =>
    ehTexto(path)
      ? { path, content: await arquivo.text(), encoding: "utf8" as const }
      : {
          path,
          content: paraBase64(new Uint8Array(await arquivo.arrayBuffer())),
          encoding: "base64" as const,
        },
  );

  confereLimites(files);

  return { folderName: raiz ?? "skill", files };
}

/** Lê um `.zip` arrastado e monta o pacote. */
export async function lerZip(arquivo: File): Promise<PacoteLido> {
  const zip = await JSZip.loadAsync(arquivo);

  /**
   * O formato ZIP exige barra NORMAL como separador, e nem todo compactador
   * obedece: o `Compress-Archive` do PowerShell grava `minha-skill\SKILL.md`.
   * Sem normalizar, o pacote inteiro vira um único caminho sem pastas — o
   * leitor não encontra raiz comum, não corta prefixo nenhum, e recusa o
   * pacote dizendo que falta o `SKILL.md`, que está lá.
   *
   * O sintoma acusa o pacote de um defeito que é do compactador, e quem o
   * recebe não tem como saber disso. Normalizar aqui é mais honesto do que
   * documentar qual ferramenta usar.
   */
  const comBarra = (nome: string) => nome.split("\\").join("/");

  const entradas = Object.values(zip.files)
    .map((entrada) => ({ entrada, nome: comBarra(entrada.name) }))
    .filter(({ entrada, nome }) => !entrada.dir && !ehLixo(nome));

  const { raiz, corte } = semRaiz(entradas.map(({ nome }) => nome));

  // A mesma janela da leitura de pasta: descompactar uma entrada por vez
  // desperdiça o paralelismo, e todas de uma vez traz o zip inteiro descompactado
  // para a memória.
  const aLer = entradas
    .map(({ entrada, nome }) => ({ entrada, path: nome.slice(corte) }))
    .filter(({ path }) => path);

  const files = await emLotes(aLer, async ({ entrada, path }) =>
    ehTexto(path)
      ? { path, content: await entrada.async("string"), encoding: "utf8" as const }
      : {
          path,
          content: paraBase64(await entrada.async("uint8array")),
          encoding: "base64" as const,
        },
  );

  confereLimites(files);

  return {
    folderName: raiz ?? arquivo.name.replace(/\.zip$/i, ""),
    files,
  };
}

/**
 * Lê uma PASTA arrastada e monta o pacote.
 *
 * `webkitGetAsEntry` é a única forma de alcançar o conteúdo de uma pasta
 * solta no navegador — o `File` da API padrão só chega em arquivo.
 */
export async function lerPasta(entrada: FileSystemDirectoryEntry): Promise<PacoteLido> {
  const files: ArquivoDoPacote[] = [];

  const percorre = async (diretorio: FileSystemDirectoryEntry, prefixo: string) => {
    const leitor = diretorio.createReader();

    // `readEntries` devolve em lotes e para quando o lote vem vazio; ler uma
    // vez só perde o que passa do primeiro lote em pasta grande.
    const lote = (): Promise<FileSystemEntry[]> =>
      new Promise((resolve, reject) => leitor.readEntries(resolve, reject));

    for (let atual = await lote(); atual.length > 0; atual = await lote()) {
      for (const item of atual) {
        const caminho = prefixo ? `${prefixo}/${item.name}` : item.name;
        if (ehLixo(caminho)) continue;

        if (item.isDirectory) {
          await percorre(item as FileSystemDirectoryEntry, caminho);
          continue;
        }

        const arquivo = await new Promise<File>((resolve, reject) =>
          (item as FileSystemFileEntry).file(resolve, reject),
        );

        files.push(
          ehTexto(caminho)
            ? { path: caminho, content: await arquivo.text(), encoding: "utf8" }
            : {
                path: caminho,
                content: paraBase64(new Uint8Array(await arquivo.arrayBuffer())),
                encoding: "base64",
              },
        );
      }
    }
  };

  await percorre(entrada, "");
  confereLimites(files);

  return { folderName: entrada.name, files };
}
